// Passport dev harness — reachable at `?view=passport`. Picks one of the 8 frozen
// datasets, runs the engine live, and renders the artboard. This is the Track A
// scaffold: it proves tokens + fonts + engine-in-browser + section render end to
// end, ahead of the wizard that will feed real input (Track B/C).

import { useState } from 'react';
import '../styles/r10.css';
import '../styles/r10-ii.css';
import '../styles/r10-iv.css';
import '../styles/r10-v.css';
import '../styles/r10-vi.css';
import '../styles/r10-vii.css';
import { DATASETS } from './datasets.js';
import { buildPassport } from './passportData.js';
import { viewI } from './viewFromPassport.js';
import Artboard from './Artboard.jsx';
import SectionI from './sections/SectionI.jsx';
import SectionII from './sections/SectionII.jsx';
import SectionIII from './sections/SectionIII.jsx';
import SectionIV from './sections/SectionIV.jsx';
import SectionV from './sections/SectionV.jsx';
import SectionVI from './sections/SectionVI.jsx';
import SectionVII from './sections/SectionVII.jsx';
import { II_BOARDS } from './sections/sectionIIBoards.js';
import { III_BOARDS } from './sections/sectionIIIBoards.js';
import { IV_BOARDS } from './sections/sectionIVBoards.js';
import { V_BOARDS } from './sections/sectionVBoards.js';
import { VI_BOARDS } from './sections/sectionVIBoards.js';
import { VII_BOARDS } from './sections/sectionVIIBoards.js';

const SECTIONS = [
  { id: 'I', mark: 'i · cover', Component: SectionI },
  // §II / §III / §IV are board-driven + self-managed (they render their own artboards),
  // so they are NOT wrapped in Artboard and read a section board rather than a dataset.
  { id: 'II', selfManaged: true, boards: II_BOARDS, Component: SectionII },
  { id: 'III', selfManaged: true, boards: III_BOARDS, Component: SectionIII },
  { id: 'IV', selfManaged: true, boards: IV_BOARDS, Component: SectionIV },
  { id: 'V', selfManaged: true, boards: V_BOARDS, Component: SectionV },
  { id: 'VI', selfManaged: true, boards: VI_BOARDS, Component: SectionVI },
  { id: 'VII', selfManaged: true, boards: VII_BOARDS, Component: SectionVII },
];

const shellStyle = {
  minHeight: '100vh',
  background: '#06070a',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 20,
  padding: '28px 0 96px',
};

const barStyle = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  color: '#8F887A',
  fontSize: 12,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const selectStyle = { background: '#13151A', color: '#E8E2D4', border: '1px solid #2A2D33', padding: '4px 8px', fontFamily: 'inherit' };

export default function PassportDev() {
  const [idx, setIdx] = useState(0);
  const [sec, setSec] = useState(0);
  const [board, setBoard] = useState(0);
  const ds = DATASETS[idx];
  const passport = buildPassport(ds.data.input, ds.data.system2 ?? null);
  const section = SECTIONS[sec];
  const Section = section.Component;

  return (
    <div style={shellStyle}>
      <div style={barStyle}>
        <span>passport dev · §</span>
        <select value={sec} onChange={(e) => setSec(Number(e.target.value))} style={selectStyle}>
          {SECTIONS.map((s, i) => (
            <option key={s.id} value={i}>{s.id}</option>
          ))}
        </select>
        {section.selfManaged ? (
          <>
            <span>· board</span>
            <select value={board} onChange={(e) => setBoard(Number(e.target.value))} style={selectStyle}>
              {section.boards.map((b, i) => (
                <option key={b.id} value={i}>{b.id}</option>
              ))}
            </select>
            <span style={{ color: '#5A554A' }}>{(section.boards[board] ?? section.boards[0]).screen}</span>
          </>
        ) : (
          <>
            <span>· dataset</span>
            <select value={idx} onChange={(e) => setIdx(Number(e.target.value))} style={selectStyle}>
              {DATASETS.map((d, i) => (
                <option key={d.id} value={i}>{d.id}</option>
              ))}
            </select>
            <span style={{ color: '#5A554A' }}>
              {passport.stageCount} stages · {passport.system1.mainPattern?.type ?? '—'}
            </span>
          </>
        )}
      </div>

      {section.selfManaged ? (
        <Section view={section.boards[board] ?? section.boards[0]} />
      ) : (
        <Artboard sectionMark={section.mark} screenLabel={`§${section.id}`} run={ds.id}>
          <Section view={viewI(passport)} />
        </Artboard>
      )}
    </div>
  );
}
